import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  CakeIcon,
  WorkIcon,
  getDaysUntil,
  getNumberSuffix,
} from 'src/utils/celebrationUtils'; 

const DEFAULT_ROTATION_SPEED = 0.4;
const SCROLL_SENSITIVITY = 0.4;
const CONTAINER_HEIGHT = 250;

interface CelebrationWheelProps {
  events: any[];
}

const CelebrationWheel = ({ events }: CelebrationWheelProps) => {
  const [scrollOffset, setScrollOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  // Adjust item height based on screen width for responsiveness
  const [itemHeight, setItemHeight] = useState(window.innerWidth < 1400 ? 60 : 55);
  const itemCount = events.length;
  const visibleCount = Math.floor(CONTAINER_HEIGHT / itemHeight);
  const centerY = itemHeight * (visibleCount / 2);
  // Wrap scroll position to create continuous loop
  const wrapScroll = (position: number, total: number) => ((position % total) + total) % total;

  useEffect(() => {
    const handleResize = () => {
      setItemHeight(window.innerWidth < 1400 ? 60 : 55);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startAnimation = useCallback(() => {
    animationRef.current = requestAnimationFrame(() => {
      setScrollOffset((prev) => (prev + DEFAULT_ROTATION_SPEED) % (itemCount * itemHeight));
      startAnimation();
    });
  }, [itemCount]);

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
  };

  // Auto-scroll the wheel animation unless hovered
  useEffect(() => {
    if (isHovered) stopAnimation();
    else if (!animationRef.current) startAnimation();
  }, [isHovered, startAnimation, stopAnimation]);

  const handleWheel = useCallback((e: WheelEvent) => {
    if (!isHovered) return;
    e.preventDefault();
    setScrollOffset((prev) => {
      let next = prev + e.deltaY * SCROLL_SENSITIVITY;
      const full = itemCount * itemHeight;
      if (next < 0) next += full;
      if (next > full) next -= full;
      return next;
    });
  }, [isHovered, itemCount]);

  // Mouse wheel scroll handler (manual control when hovered)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  return (
    <div
      className="rotating-wheel"
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {events.map((event, index) => {
        const daysUntil = getDaysUntil(event.date);
        // Calculate wheel dimensions and vertical position for infinite scroll effect
        const wheelHeight = itemCount * itemHeight;
        const unwrappedY = index * itemHeight - scrollOffset;
        const adjustedY = wrapScroll(unwrappedY, wheelHeight);
        // Calculate fade effect near the top and bottom edges of the container
        const fadeEdgeBuffer = itemHeight ;
        const fadeOutTopRatio =
          adjustedY < fadeEdgeBuffer
            ? adjustedY / fadeEdgeBuffer
            : 1;
        const fadeOutBottomRatio =
          (CONTAINER_HEIGHT - (adjustedY + itemHeight)) < fadeEdgeBuffer
            ? (CONTAINER_HEIGHT - (adjustedY + itemHeight)) / fadeEdgeBuffer
            : 1;
        // Calculate fade and opacity based on distance from the vertical center of the container
        const distanceFromCenter = Math.abs(adjustedY - centerY);
        const centerDistanceRatio = distanceFromCenter / centerY;
        const fadingRatio = Math.min(fadeOutTopRatio, fadeOutBottomRatio, 1);
        const opacity = Math.min(1 - centerDistanceRatio, fadingRatio);

        return (
          <div
            key={`${event.employeeId}-${event.label}-${index}`}
            className="celebrations-item today-celebrations-item wheel-item"
            style={{
              top: adjustedY,
              opacity,
              pointerEvents: opacity > 0.3 ? "auto" : "none",
            }}
          >
            <Avatar sx={{ bgcolor: "#1976d2", width: 35, height: 35, mx: "5px" }}>
              {event.employeeName?.split(" ").map(word => word[0]?.toUpperCase()).join("")}
            </Avatar>
            <div className="celebration-content-column">
              <div className="celebration-header-row" style={{ alignItems: "baseline" }}>
                <div>
                  {event.birthday && event.icon === CakeIcon && <span className="emp-name">Happy Birthday</span>}
                  {event.milestone && event.icon === WorkIcon && (
                    <span className="emp-name wheel-emp-name">
                      Happy {event.anniversaryCount}
                      {getNumberSuffix(event.anniversaryCount)} Anniversary
                    </span>
                  )}
                  <span className="emp-name wheel-emp-name"> {event.employeeName}</span>
                </div>
              </div>
            </div>
            <div className="celebrations-icon">
              {daysUntil > 0 ? (
                <span>
                  In {daysUntil} {daysUntil === 1 ? 'day' : 'days'}
                </span>
              ) : daysUntil === 0 ? (
                <span>Today</span>
              ) : (
                <span>
                  {Math.abs(daysUntil)} {Math.abs(daysUntil) === 1 ? 'day' : 'days'} ago
                </span>
              )}
              <event.icon sx={{ fontSize: 24, mr: 1, mb: 0.5, color: event.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CelebrationWheel;
